import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer,
    AreaChart, Area,
} from 'recharts';
import LoadingSpinner from "../components/LoadingSpinner";
import SurveyCommentsInactive from '../components/SurveyCommentsInactive';
import '../css/survey.css';
import api from "../api";

function SurveyPageInactive() {
    const { t, i18n } = useTranslation();
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSurveyIndex, setActiveSurveyIndex] = useState(0);
    const [chartType, setChartType] = useState('Bar');

    const chartTypes = ['Bar', 'Area'];

    useEffect(() => {
        const fetchInactiveSurveys = async () => {
            setLoading(true);
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/api/surveys/inactive?lang=${i18n.language}`);
                setSurveys(response.data.surveys || []);
            } catch (error) {
                console.error('Error fetching inactive surveys:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchInactiveSurveys();
    }, [i18n.language]);

    const surveyOptions = surveys.map((survey, index) => ({
        value: index,
        label: `${t('survey.survey')} ${index + 1}`
    }));

    const getXAxisProps = (questionData) => {
        if (questionData.length > 5) {
            return {
                tick: { angle: -45, textAnchor: 'end' },
                interval: 0
            };
        }
        return {};
    };

    const renderChart = (type, questionData) => {
        const xAxisProps = getXAxisProps(questionData);
        const isManyOptions = questionData.length > 5;

        const chartMargin = isManyOptions
            ? { top: 20, right: 30, left: 20, bottom: 70 }
            : { top: 20, right: 30, left: 20, bottom: 5 };

        switch (type) {
            case 'Area':
                return (
                    <AreaChart data={questionData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" {...xAxisProps} />
                        <YAxis />
                        <Tooltip cursor={{ fill: 'transparent' }} />
                        <Legend content={() => null} />
                        <Area
                            type="monotone"
                            dataKey="votes"
                            name={t('survey.votes')}
                            stroke="rgba(255, 0, 73, 0.9)"
                            fill="rgba(255, 0, 73, 0.4)"
                        />
                    </AreaChart>
                );
            default:
                return (
                    <BarChart data={questionData} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" {...xAxisProps} />
                        <YAxis />
                        <Tooltip cursor={{ fill: 'transparent' }} />
                        <Legend content={() => null} />
                        <Bar
                            dataKey="votes"
                            name={t('survey.votes')}
                            fill="rgba(255, 0, 73, 0.6)"
                        />
                    </BarChart>
                );
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (surveys.length === 0) {
        return (
            <div className="survey-page">
                <div className="survey-container">
                    <div className="section-title">
                        <span className="title-text">{t('survey.previousSurveys')}</span>
                        <span className="arrow-icon">›</span>
                    </div>
                    <p>{t('survey.noInactiveSurveys')}</p>
                </div>
            </div>
        );
    }

    const activeSurvey = surveys[activeSurveyIndex];

    return (
        <div className="survey-page">
            <div className="survey-container">
                <div className="section-title">
                    <span className="title-text">{t('survey.previousSurveys')}</span>
                    <span className="arrow-icon">›</span>
                </div>

                {surveys.length > 1 && (
                    <>
                        {surveys.length <= 5 && (
                            <div className="survey-tabs">
                                {surveys.map((survey, index) => (
                                    <button
                                        key={survey.surveyId}
                                        className={`survey-tab ${activeSurveyIndex === index ? 'active' : ''}`}
                                        onClick={() => setActiveSurveyIndex(index)}
                                    >
                                        {t('survey.survey')} {index + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                        {(surveys.length > 5 || window.innerWidth <= 768) && (
                            <div className="survey-select">
                                <Select
                                    value={surveyOptions[activeSurveyIndex]}
                                    onChange={(selectedOption) => setActiveSurveyIndex(selectedOption.value)}
                                    options={surveyOptions}
                                    classNamePrefix="survey-select"
                                    isSearchable={false}
                                />
                            </div>
                        )}
                    </>
                )}
                <div className="survey-wrapper no-top-border">
                    <div className="survey-result">
                        <aside className="survey-list">
                            <div className="survey-type-container">
                                {chartTypes.map((type) => {
                                    const label = t(`survey.chartTypes.${type}`);
                                    const isActive = chartType === type;
                                    const buttonClass = isActive
                                        ? "survey-type-btn survey-type-btn-active"
                                        : "survey-type-btn";

                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setChartType(type)}
                                            className={buttonClass}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                                <div className="total-votes-inactive">
                                    {t('survey.totalVotes', {count: activeSurvey.totalVotes || 0})}
                                </div>
                            </div>
                            {activeSurvey.questions.map(question => {
                                const questionData = question.options.map(opt => ({
                                    name: opt.title,
                                    votes: opt.votesCount
                                }));

                                return (
                                    <div key={question.questionId} className="survey-question-result">
                                        <p>{question.question}</p>
                                        <ResponsiveContainer width="100%" height={300}>
                                            {renderChart(chartType, questionData)}
                                        </ResponsiveContainer>
                                    </div>
                                );
                            })}
                        </aside>
                    </div>
                    <SurveyCommentsInactive surveyId={activeSurvey.surveyId}/>
                </div>
            </div>
        </div>
    );
}

export default SurveyPageInactive;